import { ExperimentViewOverridesSchema } from '@/types/schema';

import {
  CORE_COLUMNS,
  getColumnDisplayName,
} from '../../../utils/columnTypeUtils';

export type ExperimentViewRow = {
  id: string;
  values: {
    name?: string;
    hide?: boolean;
    precision?: number;
    color_gradient?: [number, string][];
  };
  canRemove?: boolean;
  pending?: boolean;
};

export const convertExperimentViewToRows = (
  experimentView?: ExperimentViewOverridesSchema,
  aggregateFeedbackKeys?: string[],
  noAttachments?: boolean
): ExperimentViewRow[] => {
  // Get existing columns from config
  const existingColumns = experimentView?.column_overrides ?? [];

  const coreColumns = CORE_COLUMNS.filter(
    (coreCol) => !noAttachments || coreCol !== 'attachments'
  );

  // Create rows for existing columns
  const experimentViewRows = existingColumns.map((column, i) => {
    const isReadOnly =
      coreColumns.includes(column.column) ||
      column.column.startsWith('metrics.') ||
      (column.column.startsWith('feedback.') &&
        aggregateFeedbackKeys?.includes(column.column.split('.')[1]));
    return {
      id: ['experimentview', i].join('-'),
      values: {
        name: getColumnDisplayName(column.column),
        key: column.column.toLowerCase(),
        hide: column.hide,
        precision: column.precision,
        color_gradient: column.color_gradient,
      },
      pending: !isReadOnly,
      canRemove: !isReadOnly,
    };
  });

  // Add any missing core columns
  const existingDisplayNames = existingColumns.map((col) =>
    getColumnDisplayName(col.column)
  );

  const missingCoreColumns = coreColumns.filter(
    (coreCol) => !existingDisplayNames.includes(coreCol)
  );
  const missingCoreRows = missingCoreColumns.map((coreCol, i) => ({
    id: ['experimentview', coreCol, 'missing', i].join('-'),
    values: {
      name: coreCol,
      key: coreCol.toLowerCase(),
      hide: false,
      precision: 2,
    },
    canRemove: false,
  }));

  return [...experimentViewRows, ...missingCoreRows];
};

export function addMissingDefaultColumnsToExperimentView(
  experimentView: ExperimentViewOverridesSchema,
  defaultColumnNames: string[]
): ExperimentViewOverridesSchema {
  const existingColumns = experimentView.column_overrides.map(
    (col) => col.column
  );
  const missingColumns = defaultColumnNames.filter(
    (columnName) => !existingColumns.includes(columnName)
  );

  if (missingColumns.length === 0) {
    return experimentView;
  }

  const newColumnOverrides = missingColumns.map((columnName) => ({
    column: columnName,
    hide: false,
  }));

  return {
    ...experimentView,
    column_overrides: [
      ...experimentView.column_overrides,
      ...newColumnOverrides,
    ],
  };
}
